<template>
	<loader v-if="!formReady"/>
	<form v-else class="wide" @submit.prevent>
		<v-tabs
			ref="tabs" v-slot="{ goToTab }" class="quote-breakdown tabs--switch">
			<v-tab id="quote-breakdown__summary" :tab-key="1" label="Quote Summary">
				<header class="sr-only">
					<h1 class="beta heavy">Quote Summary</h1>
				</header>
				<table class="mt-1">
					<tbody v-if="quote.calculation.purchase">
						<tr>
							<th align="left" colspan="2" scope="rowgroup">
								<div class="flex items-center content-between gap-1">
									<span class="beta">Purchase</span>
									<button
										class="button button--secondary--small"
										@click.prevent="goToTab('quote-breakdown--purchase')">Edit
									</button>
								</div>
							</th>
						</tr>
						<tr>
							<th align="left" class="light" scope="row">Fees</th>
							<td align="right" v-html="$present('price', quote.calculation.purchase.allFeesSubTotal)"/>
						</tr>
						<tr>
							<th align="left" class="light" scope="row">Disbursements</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.purchase.disbursementsSubTotal)"/>
						</tr>
						<tr>
							<th align="left" scope="row">Purchase Sub Total</th>
							<td
								align="right" class="heavy"
								v-html="$present('price', quote.calculation.purchase.subTotal)"/>
						</tr>
					</tbody>
					<tbody v-if="quote.calculation.sale">
						<tr>
							<th align="left" colspan="2" scope="rowgroup">
								<div class="flex items-center content-between gap-1">
									<span class="beta">Sale</span>
									<button
										class="button button--secondary--small"
										@click.prevent="goToTab('quote-breakdown--sale')">Edit
									</button>
								</div>
							</th>
						</tr>
						<tr>
							<th align="left" class="light" scope="row">Fees</th>
							<td align="right" v-html="$present('price', quote.calculation.sale.allFeesSubTotal)"/>
						</tr>
						<tr>
							<th align="left" class="light" scope="row">Disbursements</th>
							<td align="right" v-html="$present('price', quote.calculation.sale.disbursementsSubTotal)"/>
						</tr>
						<tr>
							<th align="left" scope="row">Sale Sub Total</th>
							<td
								align="right" class="heavy"
								v-html="$present('price', quote.calculation.sale.subTotal)"/>
						</tr>
					</tbody>
					<tbody v-if="quote.calculation.remortgage">
						<tr>
							<th align="left" colspan="2" scope="rowgroup">
								<div class="flex items-center content-between gap-1">
									<span class="beta">Remortgage</span>
									<button
										class="button button--secondary--small"
										@click.prevent="goToTab('quote-breakdown--remortgage')">Edit
									</button>
								</div>
							</th>
						</tr>
						<tr>
							<th align="left" class="light" scope="row">Fees</th>
							<td align="right" v-html="$present('price', quote.calculation.remortgage.allFeesSubTotal)"/>
						</tr>
						<tr>
							<th align="left" class="light" scope="row">Disbursements</th>
							<td
								align="right"
								v-html="$present('price', quote.calculation.remortgage.disbursementsSubTotal)"/>
						</tr>
						<tr>
							<th align="left" scope="row">Remortgage Sub Total</th>
							<td
								align="right" class="heavy"
								v-html="$present('price', quote.calculation.remortgage.subTotal)"/>
						</tr>
					</tbody>
					<tbody v-if="quote.calculation.other.disbursements">
						<tr>
							<th align="left" colspan="2" scope="rowgroup">
								<div class="flex items-center content-between gap-1">
									<span class="beta">Other Disbursements</span>
									<button
										class="button button--secondary--small"
										@click.prevent="goToTab('quote-breakdown--other')">Edit
									</button>
								</div>
							</th>
						</tr>
						<tr
							v-for="(disbursement, disbursementIndex) in quote.calculation.other.disbursements"
							v-if="disbursement.price.units">
							<th align="left" class="light" scope="row">
								<span v-html="disbursement.label"/>
								<span
									v-if="disbursement.qty > 1">x {{ disbursement.qty }} @ {{ $present("price", disbursement.price) }}</span>
							</th>
							<td align="right" v-html="$present('price', disbursement.subTotal)"/>
						</tr>
						<tr>
							<th align="left" scope="row">ID Checks Total</th>
							<td
								align="right" class="heavy"
								v-html="$present('price', quote.calculation.other.disbursementsSubTotal)"/>
						</tr>
					</tbody>
					<tbody>
						<tr>
							<th align="left" scope="row">VAT</th>
							<td
								align="right"
								class="heavy"
								v-html="$present('price', quote.calculation.tax)"/>
						</tr>
						<tr>
							<th align="left" class="beta" scope="row">Total</th>
							<td align="right" class="beta heavy" v-html="$present('price', quote.calculation.total)"/>
						</tr>
					</tbody>
				</table>
			</v-tab>
			<v-tab id="quote-breakdown__quote-requirements" :tab-key="2" label="Requirements">
				<conveyancing-fieldset-quote-type :form="form" class="fieldset"/>
				<conveyancing-fieldset-purchase :form="form" class="fieldset"/>
				<conveyancing-fieldset-sale :form="form" class="fieldset"/>
				<conveyancing-fieldset-remortgage :form="form" class="fieldset"/>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing"
								class="button button--primary" text="Update Requirements" @click.prevent="update()"/>
						</li>
					</ul>
				</div>
			</v-tab>
			<v-tab
				v-if="quote.calculation.purchase" id="quote-breakdown--purchase"
				:tab-key="3" label="Purchase">
				<header class="sr-only">
					<h1 class="beta heavy">Purchase Breakdown</h1>
				</header>
				<fieldset class="mt-2">
					<div>
						<div>
							<legend>
								<span>Purchase Fees</span>
							</legend>
						</div>
						<div class="input-group">
							<label class="inline"><span>Legal Fees </span>
								<input
									v-model="form.data.calculation.purchase.fees.fee_legal_fees"
									class="number--thousands" min="0.01" step="0.01"
									type="number"/>
							</label>
							<label
								v-for="(fee, feeIndex) in Object.keys(form.data.calculation.purchase.fees)"
								v-if="fee !== 'fee_legal_fees'"
								:key="feeIndex"
								class="inline"><span v-html="feeName(fee)"/>
								<div>
									<input
										v-model="form.data.calculation.purchase.fees[fee]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.purchase.fees', fee)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Purchase Fees Sub Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.purchase.fees))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('purchase', 'fee')">Add Fee
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend>
								<span>Purchase Disbursements</span>
							</legend>
						</div>
						<div class="input-group">
							<label
								v-for="(disbursement, disbursementIndex) in Object.keys(form.data.calculation.purchase.disbursements)"
								:key="disbursementIndex"
								class="inline"><span v-html="feeName(disbursement)"/>
								<div>
									<input
										v-model="form.data.calculation.purchase.disbursements[disbursement]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.purchase.disbursements', disbursement)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Purchase Disbursements Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.purchase.disbursements))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('purchase', 'disbursement')">Add Disbursement
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend><span>Purchase Quote</span></legend>
						</div>
						<div>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">VAT</dt>
									<dd
										class="beta"
										v-html="$present('price', vat(sum(form.data.calculation.purchase.fees)))"/>
								</div>
								<div class="flex gap-2 content-between mt-2">
									<dt class="heavy">Purchase Total</dt>
									<dd
										class="alpha"
										v-html="$present('price', sum(sum(form.data.calculation.purchase.fees), sum(form.data.calculation.purchase.disbursements), vat(sum(form.data.calculation.purchase.fees))))"/>
								</div>
							</dl>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing"
								class="button button--primary"
								text="Update Purchase Quote" @click.prevent="update()"/>
						</li>
					</ul>
				</div>
			</v-tab>
			<v-tab
				v-if="quote.calculation.sale" id="quote-breakdown--sale"
				:tab-key="4" label="Sale">
				<header class="sr-only">
					<h1 class="beta heavy">Sale Breakdown</h1>
				</header>
				<fieldset class="mt-2">
					<div>
						<div>
							<legend>
								<span>Sale Fees</span>
							</legend>
						</div>
						<div class="input-group">
							<label class="inline"><span>Legal Fees </span>
								<input
									v-model="form.data.calculation.sale.fees.fee_legal_fees"
									class="number--thousands" min="0.01" step="0.01"
									type="number"/>
							</label>
							<label
								v-for="(fee, feeIndex) in Object.keys(form.data.calculation.sale.fees)"
								v-if="fee !== 'fee_legal_fees'"
								:key="feeIndex"
								class="inline"><span v-html="feeName(fee)"/>
								<div>
									<input
										v-model="form.data.calculation.sale.fees[fee]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.sale.fees', fee)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Sale Fees Sub Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.sale.fees))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('sale', 'fee')">Add Fee
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend>
								<span>Sale Disbursements</span>
							</legend>
						</div>
						<div class="input-group">
							<label
								v-for="(disbursement, disbursementIndex) in Object.keys(form.data.calculation.sale.disbursements)"
								:key="disbursementIndex"
								class="inline"><span v-html="feeName(disbursement)"/>
								<div>
									<input
										v-model="form.data.calculation.sale.disbursements[disbursement]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.sale.disbursements', disbursement)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Sale Disbursements Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.sale.disbursements))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('sale', 'disbursement')">Add Disbursement
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend><span>Sale Quote</span></legend>
						</div>
						<div>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">VAT</dt>
									<dd
										class="beta"
										v-html="$present('price', vat(sum(form.data.calculation.sale.fees)))"/>
								</div>
								<div class="flex gap-2 content-between mt-2">
									<dt class="heavy">Sale Total</dt>
									<dd
										class="alpha"
										v-html="$present('price', sum(sum(form.data.calculation.sale.fees), sum(form.data.calculation.sale.disbursements), vat(sum(form.data.calculation.sale.fees))))"/>
								</div>
							</dl>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing"
								class="button button--primary"
								text="Update Sale Quote" @click.prevent="update()"/>
						</li>
					</ul>
				</div>
			</v-tab>
			<v-tab
				v-if="quote.calculation.remortgage" id="quote-breakdown--remortgage"
				:tab-key="5" label="Remortgage">
				<header class="sr-only">
					<h1 class="beta heavy">Remortgage Breakdown</h1>
				</header>
				<fieldset class="mt-2">
					<div>
						<div>
							<legend>
								<span>Remortgage Fees</span>
							</legend>
						</div>
						<div>
							<label class="inline"><span>Legal Fees </span>
								<input
									v-model="form.data.calculation.remortgage.fees.fee_legal_fees"
									class="number--thousands" min="0.01" step="0.01"
									type="number"/>
							</label>
							<label
								v-for="(fee, feeIndex) in Object.keys(form.data.calculation.remortgage.fees)"
								v-if="fee !== 'fee_legal_fees'"
								:key="feeIndex"
								class="inline"><span v-html="feeName(fee)"/>
								<div>
									<input
										v-model="form.data.calculation.remortgage.fees[fee]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.remortgage.fees', fee)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Remortgage Fees Sub Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.remortgage.fees))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('remortgage', 'fee')">Add Fee
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend>
								<span>Remortgage Disbursements</span>
							</legend>
						</div>
						<div class="input-group">
							<label
								v-for="(disbursement, disbursementIndex) in Object.keys(form.data.calculation.remortgage.disbursements)"
								:key="disbursementIndex"
								class="inline"><span v-html="feeName(disbursement)"/>
								<div>
									<input
										v-model="form.data.calculation.remortgage.disbursements[disbursement]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.remortgage.disbursements', disbursement)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Remortgage Disbursements Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.remortgage.disbursements))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('remortgage', 'disbursement')">Add Disbursement
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend><span>Remortgage Quote</span></legend>
						</div>
						<div>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">VAT</dt>
									<dd
										class="beta"
										v-html="$present('price', vat(sum(form.data.calculation.remortgage.fees)))"/>
								</div>
								<div class="flex gap-2 content-between mt-2">
									<dt class="heavy">Remortgage Total</dt>
									<dd
										class="alpha"
										v-html="$present('price', sum(sum(form.data.calculation.remortgage.fees), sum(form.data.calculation.remortgage.disbursements), vat(sum(form.data.calculation.remortgage.fees))))"/>
								</div>
							</dl>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing"
								class="button button--primary"
								text="Update Remortgage Quote" @click.prevent="update()"/>
						</li>
					</ul>
				</div>
			</v-tab>
			<v-tab
				v-if="quote.calculation.other" id="quote-breakdown--other"
				:tab-key="6" label="Other">
				<header class="sr-only">
					<h1 class="beta heavy">Other</h1>
				</header>
				<fieldset>
					<div>
						<div>
							<legend>
								<span>Other Disbursements</span>
							</legend>
						</div>
						<div class="input-group">
							<label
								v-for="(disbursement, disbursementIndex) in Object.keys(form.data.calculation.other.disbursements)"
								:key="disbursementIndex"
								class="inline"><span v-html="feeName(disbursement)"/>
								<div>
									<input
										v-model="form.data.calculation.other.disbursements[disbursement]"
										class="number--thousands" min="0.01" step="0.01"
										type="number"/>
									<button
										@click.prevent="form.removeIndex('calculation.other.disbursements', disbursement)">Remove
									</button>
								</div>
							</label>
							<dl>
								<div class="flex gap-2 content-between">
									<dt class="heavy">Other Disbursements Total</dt>
									<dd
										class="beta"
										v-html="$present('price', sum(form.data.calculation.other.disbursements))"/>
								</div>
							</dl>
							<ul class="actions actions--right mt-1">
								<li>
									<button
										class="button button--primary--small"
										@click.prevent="showFeeModal('other', 'disbursement')">Add Disbursement
									</button>
								</li>
							</ul>
						</div>
					</div>
				</fieldset>
				<fieldset>
					<div>
						<div>
							<legend><span>Other Quote</span></legend>
						</div>
						<div>
							<dl>
								<div class="flex gap-2 content-between mt-2">
									<dt class="heavy">Other Total</dt>
									<dd
										class="alpha"
										v-html="$present('price', sum(form.data.calculation.other.disbursements))"/>
								</div>
							</dl>
						</div>
					</div>
				</fieldset>
				<div class="actions">
					<ul>
						<li>
							<process-button
								:processing="form.processing"
								class="button button--primary"
								text="Update Other Disbursements" @click.prevent="update()"/>
						</li>
					</ul>
				</div>
			</v-tab>
		</v-tabs>
	</form>
</template>
<script>
import Address from "../../../../components/Address.vue";
import ConveyancingFieldsetPurchase from "../../../../components/forms/ConveyancingFieldsetPurchase.vue";
import ConveyancingFieldsetQuoteType from "../../../../components/forms/ConveyancingFieldsetQuoteType.vue";
import ConveyancingFieldsetRemortgage from "../../../../components/forms/ConveyancingFieldsetRemortgage.vue";
import ConveyancingFieldsetSale from "../../../../components/forms/ConveyancingFieldsetSale.vue";
import conveyancingFormFields from "../../../../constants/conveyancingFormFields";

export default {
	components: {
		Address,
		ConveyancingFieldsetPurchase,
		ConveyancingFieldsetQuoteType,
		ConveyancingFieldsetRemortgage,
		ConveyancingFieldsetSale,
	},

	data() {
		return {
			form: null,
			formReady: false,
		};
	},

	props: {
		quote: {
			type: Object,
			required: true,
		},
	},

	methods: {
		feeName(feeIndex) {
			let nameMaps = {
				"disbursement_id_checks": "ID/AML Checks",
			};
			return nameMaps[feeIndex] || feeIndex.toLowerCase()
				.replace("fee_", "")
				.replace("disbursement_", "")
				.split("_")
				.map((s) => s.charAt(0).toUpperCase() + s.substring(1))
				.join(" ");
		},
		feesSubTotal(data) {
			return this.sum(data.fees);
		},
		sum(...args) {
			let values = Array.from(_.flatten(_.map(args, arg => {
				return _.isObject(arg) ? _.values(arg) : arg;
			})));
			return _.sum(_.map(values, value => {
				return parseFloat(value);
			}));
		},
		vat(value) {
			return value * .2;
		},
		showFeeModal(transaction, type) {
			this.$vmodal.open({
				component: require("./FeeModal.vue").default,
				props: {
					transaction: transaction,
					type: type,
					quoteForm: this.form,
					feeName: this.feeName,
					set: (field, value, index) => {
						this.form.set(field, value, index);
						this.form = this.newForm(this.form.getData());
					},
				},
			});
		},
		calculation() {
			let calculation = {
				sale: {
					// fee_legal: this.quote.calculation.sale?.legalFeesSubTotal?.float,
					fees: _.mapValues(this.quote.calculation.sale?.fees || {}, fee => {
						return fee.price.float;
					}),
					disbursements: _.mapValues(this.quote.calculation.sale?.disbursements || {}, disbursement => {
						return disbursement.price.float;
					}),
				},
				purchase: {
					// fee_legal: this.quote.calculation.purchase?.legalFeesSubTotal?.float,
					fees: _.mapValues(this.quote.calculation.purchase?.fees || {}, fee => {
						return fee.price.float;
					}),
					disbursements: _.mapValues(this.quote.calculation.purchase?.disbursements || {}, disbursement => {
						return disbursement.price.float;
					}),
				},
				remortgage: {
					// fee_legal: this.quote.calculation.remortgage?.legalFeesSubTotal?.float,
					fees: _.mapValues(this.quote.calculation.remortgage?.fees || {}, fee => {
						return fee.price.float;
					}),
					disbursements: _.mapValues(this.quote.calculation.remortgage?.disbursements || {}, disbursement => {
						return disbursement.price.float;
					}),
				},
				other: {
					disbursements: _.mapValues(this.quote.calculation.other?.disbursements || {}, disbursement => {
						return disbursement.price.float;
					}),
				},
			};
			calculation.sale.fees.fee_legal_fees = this.quote.calculation.sale?.legalFeesSubTotal?.float;
			calculation.purchase.fees.fee_legal_fees = this.quote.calculation.purchase?.legalFeesSubTotal?.float;
			calculation.remortgage.fees.fee_legal_fees = this.quote.calculation.remortgage?.legalFeesSubTotal?.float;
			return calculation;
		},
		update() {
			this.form.patch(this.route("quotes/:quote", this.quote)).then(response => {
				this.eventBus.$handle("quoteUpdated", response.data);
				this.fillForm();
				this.refs.tabs.goToTab("quote-breakdown__summary");
			});
		},
		fillForm() {
			console.log("fillForm", this.calculation());
			this.form.reset();
			this.form.prefill({
				data: {
					conveyancing: _.mapValues(this.quote.service_requirements, service => {
						if (!service.property_price) {
							return service;
						}

						service.property_price = service.property_price.wholes;
						return service;
					}),
					calculation: this.calculation(),
				},
			});
			this.form.set("calculation", this.calculation());
		},
	},

	watch: {
		quote() {
			console.log("quote changed");
			this.fillForm();
		},
	},

	created() {
		http().get("form-data/quote").then(response => {
			// let calculation = this.calculation();
			let formData = {
				data: {
					conveyancing: conveyancingFormFields,
				},
				calculation: this.calculation(),
			};
			this.form = this.newForm(formData);
			this.form.setOptions(response.data);
			this.fillForm();

			this.formReady = true;
		});
	},
};
</script>
